import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Users, FileText, AlertTriangle } from "lucide-react";

export default function HR() {
  return (
    <div className="space-y-6" data-testid="hr-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Recursos Humanos</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Recursos Humanos</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Empleados, documentos del personal, clientes y proveedores con control de vencimientos.</p>
      </div>
      <Tabs defaultValue="empleados">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="empleados"><Users size={14} className="mr-2" />Empleados</TabsTrigger>
          <TabsTrigger value="documentos"><FileText size={14} className="mr-2" />Documentos</TabsTrigger>
          <TabsTrigger value="alertas"><AlertTriangle size={14} className="mr-2" />Vencimientos</TabsTrigger>
        </TabsList>
        <TabsContent value="empleados"><Empleados /></TabsContent>
        <TabsContent value="documentos"><Documentos /></TabsContent>
        <TabsContent value="alertas"><AlertasDocs /></TabsContent>
      </Tabs>
    </div>
  );
}

function Empleados() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  function empty() { return { numero_identificacion: "", tipo_identificacion: "cedula", nombres: "", apellidos: "", cargo: "", area: "", email: "", telefono: "", direccion: "", fecha_ingreso: "", salario: 0, activo: true }; }

  const load = async () => { const r = await api.get("/hr/empleados"); setItems(r.data); };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const save = async () => {
    try {
      if (editing) await api.patch(`/hr/empleados/${editing.id}`, form);
      else await api.post("/hr/empleados", form);
      toast.success("Guardado"); setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (e) => { if (!window.confirm(`¿Eliminar ${e.nombres}?`)) return; await api.delete(`/hr/empleados/${e.id}`); load(); };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><Button onClick={() => { setEditing(null); setForm(empty()); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="create-empleado-btn"><Plus size={16} className="mr-2" />Nuevo empleado</Button></div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Identificación</th><th>Nombre</th><th>Cargo</th><th>Área</th><th>F. Ingreso</th><th>Estado</th><th className="w-16"></th></tr></thead>
          <tbody>
            {items.map(e => (
              <tr key={e.id}>
                <td className="font-mono text-xs">{e.numero_identificacion}</td>
                <td className="font-medium">{e.nombres} {e.apellidos}</td>
                <td>{e.cargo || "—"}</td>
                <td>{e.area || "—"}</td>
                <td>{e.fecha_ingreso || "—"}</td>
                <td>{e.activo ? <Badge className="fefo-safe border">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}</td>
                <td><Button size="icon" variant="ghost" onClick={() => remove(e)}><Trash2 size={14} className="text-[#b53a25]" /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin empleados registrados</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} empleado</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo ID</Label>
              <Select value={form.tipo_identificacion} onValueChange={(v) => setForm({...form, tipo_identificacion: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cedula">Cédula</SelectItem><SelectItem value="pasaporte">Pasaporte</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Número</Label><Input value={form.numero_identificacion} onChange={(e) => setForm({...form, numero_identificacion: e.target.value})} data-testid="emp-id-input" /></div>
            <div><Label>Nombres</Label><Input value={form.nombres} onChange={(e) => setForm({...form, nombres: e.target.value})} /></div>
            <div><Label>Apellidos</Label><Input value={form.apellidos} onChange={(e) => setForm({...form, apellidos: e.target.value})} /></div>
            <div><Label>Cargo</Label><Input value={form.cargo} onChange={(e) => setForm({...form, cargo: e.target.value})} /></div>
            <div><Label>Área</Label><Input value={form.area} onChange={(e) => setForm({...form, area: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
            <div><Label>Teléfono</Label><Input value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} /></div>
            <div className="col-span-2"><Label>Dirección</Label><Input value={form.direccion} onChange={(e) => setForm({...form, direccion: e.target.value})} /></div>
            <div><Label>Fecha ingreso</Label><Input type="date" value={form.fecha_ingreso} onChange={(e) => setForm({...form, fecha_ingreso: e.target.value})} /></div>
            <div><Label>Salario</Label><Input type="number" step="0.01" value={form.salario} onChange={(e) => setForm({...form, salario: parseFloat(e.target.value)||0})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-empleado-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Documentos() {
  const [items, setItems] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [provs, setProvs] = useState([]);
  const [filter, setFilter] = useState("empleado");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty());
  function empty() { return { entidad_tipo: "empleado", entidad_id: "", tipo_documento: "", nombre: "", fecha_emision: "", fecha_vencimiento: "", archivo_url: "", observaciones: "" }; }

  const load = async () => {
    const [d, e, c, p] = await Promise.all([
      api.get(`/hr/documentos?entidad_tipo=${filter}`),
      api.get("/hr/empleados"),
      api.get("/masters/clientes"),
      api.get("/masters/proveedores"),
    ]);
    setItems(d.data); setEmpleados(e.data); setClientes(c.data); setProvs(p.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, [filter]);

  const entityOptions = () => {
    if (form.entidad_tipo === "empleado") return empleados.map(e => ({ id: e.id, label: `${e.nombres} ${e.apellidos}` }));
    if (form.entidad_tipo === "cliente") return clientes.map(c => ({ id: c.id, label: c.razon_social }));
    return provs.map(p => ({ id: p.id, label: p.razon_social }));
  };

  const save = async () => {
    try { await api.post("/hr/documentos", form); toast.success("Documento registrado"); setDialogOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (d) => { if (!window.confirm(`¿Eliminar ${d.nombre}?`)) return; await api.delete(`/hr/documentos/${d.id}`); load(); };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px]" data-testid="doc-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="empleado">Empleados</SelectItem>
            <SelectItem value="cliente">Clientes</SelectItem>
            <SelectItem value="proveedor">Proveedores</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => { setForm(empty()); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="create-doc-btn"><Plus size={16} className="mr-2" />Nuevo documento</Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Tipo</th><th>Nombre</th><th>Emisión</th><th>Vencimiento</th><th className="w-16"></th></tr></thead>
          <tbody>
            {items.map(d => (
              <tr key={d.id}>
                <td><Badge variant="outline">{d.tipo_documento}</Badge></td>
                <td className="font-medium">{d.nombre}</td>
                <td>{d.fecha_emision || "—"}</td>
                <td>{d.fecha_vencimiento || "—"}</td>
                <td><Button size="icon" variant="ghost" onClick={() => remove(d)}><Trash2 size={14} className="text-[#b53a25]" /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#8ba397]">Sin documentos</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuevo documento</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Entidad</Label>
              <Select value={form.entidad_tipo} onValueChange={(v) => setForm({...form, entidad_tipo: v, entidad_id: ""})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="empleado">Empleado</SelectItem><SelectItem value="cliente">Cliente</SelectItem>
                  <SelectItem value="proveedor">Proveedor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{form.entidad_tipo}</Label>
              <Select value={form.entidad_id} onValueChange={(v) => setForm({...form, entidad_id: v})}>
                <SelectTrigger><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent>{entityOptions().map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tipo documento</Label><Input value={form.tipo_documento} onChange={(e) => setForm({...form, tipo_documento: e.target.value})} placeholder="RUC, Registro sanitario, etc." /></div>
            <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} /></div>
            <div><Label>Fecha emisión</Label><Input type="date" value={form.fecha_emision} onChange={(e) => setForm({...form, fecha_emision: e.target.value})} /></div>
            <div><Label>Vencimiento</Label><Input type="date" value={form.fecha_vencimiento} onChange={(e) => setForm({...form, fecha_vencimiento: e.target.value})} /></div>
            <div className="col-span-2"><Label>URL archivo (opcional)</Label><Input value={form.archivo_url} onChange={(e) => setForm({...form, archivo_url: e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-doc-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AlertasDocs() {
  const [items, setItems] = useState([]);
  const [dias, setDias] = useState(60);
  useEffect(() => { api.get(`/hr/alertas-documentos?dias=${dias}`).then(r => setItems(r.data)); }, [dias]);
  return (
    <div className="mt-4 space-y-4">
      <div className="max-w-xs"><Label>Ventana (días)</Label><Input type="number" value={dias} onChange={(e) => setDias(parseInt(e.target.value)||60)} /></div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Entidad</th><th>Tipo</th><th>Nombre</th><th>Vencimiento</th><th>Estado</th></tr></thead>
          <tbody>
            {items.map(d => (
              <tr key={d.id}>
                <td><Badge variant="outline">{d.entidad_tipo}</Badge></td>
                <td>{d.tipo_documento}</td>
                <td className="font-medium">{d.nombre}</td>
                <td>{d.fecha_vencimiento}</td>
                <td>{d.vencido ? <Badge className="fefo-vencido border">Vencido</Badge> : <Badge className="fefo-critico border">Por vencer</Badge>}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#8ba397]">Sin documentos por vencer en {dias} días</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
